import React from 'react';
import { ArrowDown, Download, Mail, Code2 } from 'lucide-react';
import { Button } from './ui/button';
import { profileData, statsData } from '../data/mockData';

const Hero = () => {
  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="min-h-screen flex items-center justify-center relative bg-gradient-to-br from-gray-50 via-white to-emerald-50/30 pt-20">
      {/* Background Pattern */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-20 w-72 h-72 bg-emerald-100/40 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-gray-100/60 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div className="order-2 lg:order-1 text-center lg:text-left">
            <div className="inline-flex items-center px-4 py-2 bg-emerald-50 rounded-full text-emerald-700 text-sm font-medium mb-6">
              <Code2 className="w-4 h-4 mr-2" />
              Building Products That Matter
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light text-gray-900 leading-tight mb-4">
              Hi, I'm{' '}
              <span className="font-semibold text-emerald-600">{profileData.name}</span>
            </h1>
            
            <p className="text-xl sm:text-2xl text-gray-600 font-light mb-6">
              {profileData.title}
            </p>
            
            <p className="text-gray-500 text-lg leading-relaxed mb-8 max-w-xl mx-auto lg:mx-0">
              {profileData.tagline} with {statsData[0].value} years of expertise in building 
              scalable mobile and web applications that impact millions of users.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-12">
              <a href={profileData.cvUrl} download="Kailash_Kumar_CV.pdf">
                <Button
                  size="lg"
                  className="bg-gray-900 hover:bg-gray-800 text-white px-8 py-6 text-base font-medium transition-all duration-300 hover:scale-105 w-full sm:w-auto"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Download Resume
                </Button>
              </a>
              <Button
                variant="outline"
                size="lg"
                onClick={scrollToContact}
                className="border-gray-300 text-gray-700 hover:bg-gray-50 px-8 py-6 text-base font-medium transition-all duration-300 w-full sm:w-auto"
              >
                <Mail className="w-5 h-5 mr-2" />
                Get In Touch
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
              {statsData.map((stat, index) => (
                <div key={index} className="text-center lg:text-left">
                  <div className="text-2xl sm:text-3xl font-bold text-gray-900">{stat.value}</div>
                  <div className="text-sm text-gray-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Content - Photo */}
          <div className="order-1 lg:order-2 flex justify-center">
            <div className="relative">
              {/* Decorative Elements */}
              <div className="absolute -inset-4 bg-gradient-to-br from-emerald-200/30 to-gray-200/30 rounded-3xl blur-2xl" />
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-emerald-500/10 rounded-2xl" />
              <div className="absolute -top-6 -left-6 w-20 h-20 bg-gray-200/50 rounded-2xl" />
              
              {/* Profile Image - Modern Card Style */}
              <div className="relative w-72 sm:w-80 lg:w-96 rounded-2xl overflow-hidden border-4 border-white shadow-2xl">
                <img
                  src={profileData.photo}
                  alt={profileData.name}
                  className="w-full h-auto object-cover"
                />
                
                {/* Gradient Overlay at Bottom */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-gray-900/80 via-gray-900/40 to-transparent p-6">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-emerald-400 rounded-full" />
                    <span className="text-white text-sm font-medium">Tech Lead</span>
                  </div>
                  <p className="text-gray-300 text-xs mt-1">React Native • React Js • Next Js</p>
                </div>
              </div>
              
              {/* Floating Experience Badge */}
              <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-lg px-4 py-3 border border-gray-100">
                <div className="text-2xl font-bold text-emerald-600">7.5+</div>
                <div className="text-xs text-gray-500">Years Exp.</div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 hidden lg:block">
          <button
            onClick={() => document.querySelector('#about').scrollIntoView({ behavior: 'smooth' })}
            className="flex flex-col items-center text-gray-400 hover:text-gray-600 transition-colors"
          >
            <span className="text-xs mb-2">Scroll Down</span>
            <ArrowDown className="w-5 h-5 animate-bounce" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default Hero;
